import { lazy, Suspense, useEffect, useState } from 'react';
import { Await, useFetcher } from 'react-router';
import type { CountryLookup } from '@/lib/country-suggestion';

const CountrySuggestionDialog = lazy(() => import('./country-suggestion-dialog'));

function RememberDetectedCountry({ country }: { country: string }) {
    const fetcher = useFetcher();
    useEffect(() => {
        void fetcher.submit({ country }, { method: 'POST', action: '/action/remember-country-geolocation' });
        // Submit once per detected country; `fetcher` changes identity as its state updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country]);
    return null;
}

export function CountrySuggestion({ lookup }: { lookup: Promise<CountryLookup> }) {
    const [hydrated, setHydrated] = useState(false);
    useEffect(() => setHydrated(true), []);

    // This automatic prompt is requested on arrival. Mount its lazy overlay only
    // after hydration and a supported mismatch; it never occupies document flow.
    if (!hydrated) return null;
    return (
        <Suspense fallback={null}>
            <Await resolve={lookup} errorElement={null}>
                {({ suggestion, detectedCountry }: CountryLookup) => (
                    <>
                        {detectedCountry && <RememberDetectedCountry country={detectedCountry} />}
                        {suggestion && (
                            <Suspense fallback={null}>
                                <CountrySuggestionDialog
                                    key={`${suggestion.currentCountryCode}-${suggestion.countryCode}`}
                                    suggestion={suggestion}
                                />
                            </Suspense>
                        )}
                    </>
                )}
            </Await>
        </Suspense>
    );
}
